import type { SwitchingEuropeProps } from '@/components/home/SwitchingEurope';
import { BottomCTAProps } from '@/components/homeowners/BottomCTA';
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
import type { FaqProps } from '@/components/homeowners/faq/Faq';
import { faqInfo } from '@/components/homeowners/faq/FaqDataFrFr';
import type { HeroProps } from '@/components/homeowners/Hero';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { PRODUCT_HOME_EU_HOMEOWNERS } from '@/components/shared/BuildByYou/defaults';
import { sharedConfig } from '@/pageConfig/shared';
import damageImgLg from '@/static/assets/homeowners/coverage/fr/lg/damaged.svg';
import theftImgLg from '@/static/assets/homeowners/coverage/fr/lg/emergency.svg';
import leaksImgLg from '@/static/assets/homeowners/coverage/fr/lg/leaks.svg';
import lawImgLg from '@/static/assets/homeowners/coverage/fr/lg/legal.svg';
import fallenTreeImgLg from '@/static/assets/homeowners/coverage/fr/lg/property_liability.svg';
import unlivableImgLg from '@/static/assets/homeowners/coverage/fr/lg/unlivable.svg';
import damageImgSm from '@/static/assets/homeowners/coverage/fr/sm/damaged.svg';
import theftImgSm from '@/static/assets/homeowners/coverage/fr/sm/emergency.svg';
import leaksImgSm from '@/static/assets/homeowners/coverage/fr/sm/leaks.svg';
import lawImgSm from '@/static/assets/homeowners/coverage/fr/sm/legal.svg';
import fallenTreeImgSm from '@/static/assets/homeowners/coverage/fr/sm/property_liability.svg';
import unlivableImgSm from '@/static/assets/homeowners/coverage/fr/sm/unlivable.svg';
import houseLg from '@/static/assets/homeowners/houses/hero_fr/desktop.svg';
import houseSm from '@/static/assets/homeowners/houses/hero_fr/mobile.svg';
import ogImage from '@/static/assets/homeowners/og-fr.png';
import windowImage from '@/static/assets/homeowners/window_FR.svg';
import chatFrDesktopLottieConfig from '@/static/genericProducts/chatFrDesktopLottie.json';
import chatFrLottieConfig from '@/static/genericProducts/chatFrLottie.json';
import mapDesktopLottieConfig from '@/static/genericProducts/mapDesktopLottie.json';
import mapLottieConfig from '@/static/genericProducts/mapLottie.json';
import successDesktopLottieConfig from '@/static/genericProducts/successDesktopLottie.json';
import successLottieConfig from '@/static/genericProducts/successLottie.json';
import type { BasePageConfig } from '../../BasePageConfig';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade assurance habitation',
  description: "Contrats d'assurance pour les propriétaires en France",
  image: [
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners_mobile-af50b03b1ecfb81df02efe6a2a19bf7e4e07b565751c22483da27f612caeadb1.svg',
    'https://www.lemonade.com/assets/home/homeowners/right_homeowners-af94926a22ed9f66fbabc8d23a6f705b1c22ea0de0b82e0de3089f081bf8b13a.svg',
  ],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '10',
    highPrice: '',
    priceCurrency: 'EUR',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Que couvre l’assurance habitation pour propriétaires ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Notre assurance habitation se décompose en trois parties : la couverture de la structure de la maison que vous possédez, la couverture des objets que vous possédez, et la couverture de votre responsabilité civile si vous causez accidentellement des dommages ou des blessures à quelqu'un d'autre.",
      },
    },
    {
      '@type': 'Question',
      name: 'Pour quels types de sinistres suis-je couvert(e) ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Nous couvrons les dommages causés à votre habitation et à vos biens par le feu, la fumée, les explosions, les surtensions, les événements climatiques, et les dégâts des eaux. Pour plus de détails sur ce qui est couvert, vous pouvez créer un devis gratuit ou consulter notre police.',
      },
    },
    {
      '@type': 'Question',
      name: 'Combien coûte une assurance habitation pour propriétaires ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "L'assurance habitation pour propriétaires chez Lemonade commence à 10 € /mois, mais varie en fonction de facteurs tels que l'âge de votre logement, le coût de la reconstruction, les matériaux de construction, ainsi que les garanties et garanties supplémentaires que vous choisissez.",
      },
    },
    {
      '@type': 'Question',
      name: "Qu'est-ce que le réseau de réparation ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Si, après un sinistre, votre logement a besoin d'être réparé, Lemonade vous donnera accès à un réseau de réparation comprenant un large éventail de prestataires en France qui peuvent vous aider à réparer certains éléments de votre logement.",
      },
    },
    {
      '@type': 'Question',
      name: 'Comment recevoir mon attestation d’assurance habitation ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vous recevez votre attestation d’assurance habitation Lemonade par email dès votre souscription, mais en cas de doute et pour toujours l’avoir dans votre poche, vous pouvez également la télécharger à nouveau depuis l’app!',
      },
    },
    {
      '@type': 'Question',
      name: 'Quelles garanties supplémentaires puis-je souscrire ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Parmi les garanties supplémentaires que vous pouvez ajouter, on retrouve la garantie contre le cambriolage et le vandalisme, la garantie complète contre le vol, la garantie bris de glace, l’assurance scolaire, ou encore la protection juridique.',
      },
    },
    {
      '@type': 'Question',
      name: 'Mes dépendances sont-elles couvertes ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Lors de votre devis, notre chatbot Maya vous demandera toutes les informations dont nous avons besoin concernant votre habitation. En règle générale, si vous déclarez votre garage, votre abri de jardin, votre débarras, votre piscine, votre cheminée, votre panneau solaire et votre véranda lors du devis, c’est couvert!',
      },
    },
    {
      '@type': 'Question',
      name: 'Comment déclarer un sinistre ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Facile! Il suffit d'ouvrir l'app Lemonade et d'appuyer sur le bouton Sinistres. Nous vous guiderons dans le reste de la procédure. Si vous avez besoin d’aide, n'hésitez pas à nous envoyer un email à contact@lemonade.com <a href="mailto:contact@lemonade.com">contact@lemonade.com</a>.`,
      },
    },
    {
      '@type': 'Question',
      name: 'Comment vous contacter ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Vous pouvez contacter notre équipe à tout moment via notre app et notre site web, ou n'hésitez pas à envoyer un email à <a href="mailto:contact@lemonade.com">contact@lemonade.com</a>. Vous pouvez également nous appeler au <a href="tel:+33801840505">+33 801 840505</a>. En cas d’urgence uniquement et si vous souhaitez contacter nos services d’assistance, appelez nos partenaires d’assistance au : <a href="tel:+33801840506">+33 801 840506</a>.`,
      },
    },
  ],
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  coverage: CoverageProps;
  bundleSection?: unknown;
  faq: FaqProps;
  bottomCTA: BottomCTAProps;
  switchingEurope: SwitchingEuropeProps;
}

export const config: Config = {
  layout: {
    product: PRODUCT_HOME_EU_HOMEOWNERS,
    ...sharedConfig['fr-FR'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['fr-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['fr-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['fr-FR'].layout.headerActions.CTAs.checkOurPrices,
          customUrl: '/onboarding/start?f=1&ownership=owner',
          short: {
            singular: 'Consultez nos prix',
            plural: 'Consultez nos prix',
          },
        },
      },
    },
  },
  seo: {
    title: 'Assurance habitation Lemonade - Devis gratuit en quelques minutes',
    metaTitle: 'Assurance habitation Lemonade - Devis gratuit en quelques minutes',
    description:
      ' Souscrivez à une assurance habitation qui vous convient vraiment. Prix compétitifs. Remboursements éclairs. A partir de 10 € /mois.',
    ogImage: ogImage.src,
    jsonld: [productJsonld, faqSchema],
    uniqueCanonicalUrl: 'https://www.lemonade.com/fr/assurance-habitation',
  },
  hero: {
    title: 'L’assurance habitation\npréférée des propriétaires',
    subtitle: 'Simple. Rapide. À petits prix.',
    ctaLabel: 'CONSULTEZ NOS PRIX',
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
    frontHouseImage: {
      lg: houseLg.src,
      sm: houseSm.src,
      alt: 'Lemonade assurance habitation',
      houseTranslateY: {
        lg: -10,
        sm: -10,
      },
    },
  },
  customersReviews: {
    title: 'Plus de 2 000 000 clients satisfaits !',
    subtitle:
      'Lemonade est notée 4,9 étoiles sur l’App Store, et classée\nparmi les meilleures assurances par <a href="https://fr.trustpilot.com/review/lemonade.com" target="_blank">Truspilot</a>',
    darkBackground: true,
    showFullStars: true,
  },
  coverage: {
    title: "Que couvre l'assurance habitation pour propriétaires ?",
    subtitle: 'Une protection de qualité pour votre logement et vos affaires',
    data: [
      {
        title: 'Si votre maison\nest endommagée',
        description: 'Nous prenons en charge les frais de reconstruction de votre logement.',
        img: {
          src: {
            lg: damageImgLg,
            sm: damageImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'Responsabilité\nCivile',
        description:
          'Vous serez couvert(e) si un accident dans votre logement (incendie, inondation, fuite ou explosion) cause des dommages au logement de votre voisin.',
        img: {
          src: {
            lg: fallenTreeImgLg,
            sm: fallenTreeImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'Si votre logement\nest inhabitable',
        description: "Nous prendrons en charge les frais jusqu'à ce que vous soyez remis sur pied.",
        img: {
          src: {
            lg: unlivableImgLg,
            sm: unlivableImgSm,
          },
          alt: '',
        },
      },
      {
        title: "En cas de\nfuite d'eau",
        description:
          'Nous prenons en charge la recherche et la réparation des fuites provenant de votre toit, de votre façade ou des joints de votre salle de bain.',
        img: {
          src: {
            lg: leaksImgLg,
            sm: leaksImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'Si vous avez\nune urgence',
        description:
          "Nous vous fournirons une assistance 24 heures sur 24 et 7 jours sur 7 en cas d'urgence à votre domicile.",
        img: {
          src: {
            lg: theftImgLg,
            sm: theftImgSm,
          },
          alt: '',
        },
      },
      {
        title: 'Si vous avez besoin\nde protection juridique',
        description:
          "Si vous avez besoin d'un avocat pour vous défendre, nous couvrons ces frais grâce à notre protection juridique.",
        img: {
          src: {
            lg: lawImgLg,
            sm: lawImgSm,
          },
          alt: '',
        },
      },
    ],
  },
  bottomCTA: {
    title: 'Une assurance à prix mini',
    subtitle: 'Assurance habitation pour propriétaires à partir de 10 € /mois',
    ctaLabel: 'CONSULTEZ NOS PRIX',
    windowImage,
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
  },
  faq: {
    layout: {
      mt: -22,
    },
    darkBackground: true,
    title: 'Bon à savoir...',
    subtitle:
      "L’assurance habitation peut prêter à confusion, c’est pourquoi nous l’avons rendue simple. Pour en savoir plus, consultez nos guides complets sur l'assurance habitation.",
    faqInfo,
    showDisclaimer: false,
  },
  switchingEurope: {
    title: 'Déjà assuré(e) ?\nChangez pour Lemonade instantanément',
    ctaLabel: "CHANGEZ D'ASSURANCE",
    ctaUrl: '/onboarding/start?f=1&ownership=owner',
    leftLottieConfig: chatFrLottieConfig,
    centerLottieConfig: mapLottieConfig,
    rightLottieConfig: successLottieConfig,
    // desktop
    leftDesktopLottieConfig: chatFrDesktopLottieConfig,
    centerDesktopLottieConfig: mapDesktopLottieConfig,
    rightDesktopLottieConfig: successDesktopLottieConfig,
    steps: [
      'Souscrivez votre\ncontrat Lemonade',
      'Partagez les détails\nde votre ancien contrat',
      `Nous nous occuperons\nde la <a aria-label="Nous nous occuperons de la résiliation pour vous" href="https://www.lemonade.com/fr/blog/comment-resilier-votre-assurance-habitation/" target="_blank">résiliation</a>\npour vous`,
    ],
  },
  products: {
    title: 'Consultez nos produits\nd’assurance habitation chez Lemonade',
    darkBackground: true,
    trackingLabel: 'homeowners',
    products: [
      {
        title: 'Locataires',
        subtitle: 'La couverture de vos biens chez vous et à l’extérieur',
        price: 'À PARTIR DE 4 € /MOIS',
        product: 'home_eu_renters',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'assurance habitation locataire',
      },
      {
        title: 'PNO',
        subtitle: 'Protégez votre logement en tant que propriétaire non-occupant',
        price: 'À PARTIR DE 6 € /MOIS',
        product: 'landlords_eu',
        ctaLabelDesktop: 'consultez nos prix',
        ctaLabelMobile: 'prix',
        customUrl: '/onboarding/start?f=1&ownership=owner',
        alt: 'assurance PNO',
      },
    ],
  },
};
